import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2, Star, DollarSign, CheckCircle2 } from "lucide-react";

const blank = { name: "", monthly_price: 499, claim_fee: 100, drone_fee: 250, active: true, is_default: false };

// Admin: create/edit/activate/deactivate/delete membership plans + set defaults.
export default function AdminMembershipPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/membership-plans").then(({ data }) => setPlans(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Enter a plan name."); return; }
    setBusy(true);
    try {
      await api.post("/admin/membership-plans", {
        name: form.name.trim(), monthly_price: Number(form.monthly_price),
        claim_fee: Number(form.claim_fee), drone_fee: Number(form.drone_fee),
        active: form.active, is_default: form.is_default,
      });
      toast.success("Plan created"); setForm(blank); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const patch = async (id, updates) => {
    try { await api.put(`/admin/membership-plans/${id}`, updates); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/membership-plans/${id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-membership-plans">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-cobalt" /> New membership plan</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <label className="col-span-2 block"><span className="text-xs text-slate-500">Plan name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" placeholder="Founder / Professional / Enterprise" className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
          {[["Monthly", "monthly_price"], ["Claim", "claim_fee"], ["Drone", "drone_fee"]].map(([l, k]) => (
            <label key={k} className="block"><span className="text-xs text-slate-500">{l} $</span>
              <input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`plan-${k}`} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cobalt" /></label>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 accent-cobalt" data-testid="plan-default" /> Set as global default</label>
          <button onClick={create} disabled={busy} data-testid="plan-create-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create plan</button>
        </div>
      </div>

      <div className="space-y-3">
        {plans.map((p) => (
          <PlanRow key={p.plan_id} plan={p} onPatch={patch} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}

function PlanRow({ plan: p, onPatch, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ name: p.name, monthly_price: p.monthly_price, claim_fee: p.claim_fee, drone_fee: p.drone_fee });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await onPatch(p.plan_id, { name: f.name, monthly_price: Number(f.monthly_price), claim_fee: Number(f.claim_fee), drone_fee: Number(f.drone_fee) });
    setSaving(false); setEdit(false); toast.success("Plan updated");
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`plan-${p.plan_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-navy flex items-center gap-2">{p.name}
            {p.tier && <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full uppercase">{p.tier}</span>}
            {p.is_default && <span className="text-[10px] font-semibold bg-cobalt-light text-cobalt px-2 py-0.5 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> Default</span>}
            {!p.active && <span className="text-[10px] font-semibold bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full">Inactive</span>}
          </div>
          {!edit && (
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
              <span className="flex items-center gap-0.5"><DollarSign className="w-3 h-3" />{p.monthly_price}/mo</span>
              <span>Claim ${p.claim_fee}</span><span>Drone ${p.drone_fee}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setEdit(!edit)} data-testid={`plan-edit-${p.plan_id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light">{edit ? "Close" : "Edit price"}</button>
          {!p.is_default && <button onClick={() => onPatch(p.plan_id, { is_default: true })} data-testid={`plan-set-default-${p.plan_id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">Make default</button>}
          <button onClick={() => onPatch(p.plan_id, { active: !p.active })} data-testid={`plan-toggle-${p.plan_id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">{p.active ? "Deactivate" : "Activate"}</button>
          {!p.is_default && <button onClick={() => onDelete(p.plan_id)} data-testid={`plan-delete-${p.plan_id}`} className="p-2 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      {edit && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end" data-testid={`plan-edit-form-${p.plan_id}`}>
          <label className="block col-span-2 sm:col-span-1"><span className="text-xs text-slate-500">Name</span>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt" /></label>
          {[["Monthly $", "monthly_price"], ["Claim $", "claim_fee"], ["Drone $", "drone_fee"]].map(([l, k]) => (
            <label key={k} className="block"><span className="text-xs text-slate-500">{l}</span>
              <input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`plan-edit-${k}-${p.plan_id}`} className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt" /></label>
          ))}
          <div className="col-span-2 sm:col-span-4 flex justify-end">
            <button onClick={save} disabled={saving} data-testid={`plan-save-${p.plan_id}`} className="flex items-center gap-2 px-5 py-2 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save prices</button>
          </div>
        </div>
      )}
    </div>
  );
}
